const IFRAME_HOSTS = {
	vimeo: "player.vimeo.com",
	youtube: "www.youtube-nocookie.com",
};

export const emitVideoPauseEvent = (el) => {
	const video = el.querySelector("video");
	const iframe = el.querySelector("iframe");
	const event = new Event("js-video-pause");
	if (video) {
		video.dispatchEvent(event);
	}
	if (iframe) {
		iframe.dispatchEvent(event);
	}
};

export const emitVideoStopEvent = (el) => {
	const video = el.querySelector("video");
	const iframe = el.querySelector("iframe");
	const event = new Event("js-video-stop");
	if (video) {
		video.dispatchEvent(event);
	}
	if (iframe) {
		iframe.dispatchEvent(event);
	}
};

export default class VideoThumbs {
	constructor(config) {
		this.el = config.el;
		this.videoTopImage = this.el.querySelector(".video-thumbs__container");
		this.playerEl = null;
		this.YTPlayer = {};
		this.vimeoPlayer = null;
		this.videoType = "";
		this.autoPlayVideo = this.el.dataset.autoplay === "true";
		this.init();
	}

	init() {
		this.setVideoTypeAndPlayerEl();
		this.addEventListeners();
	}

	setVideoTypeAndPlayerEl() {
		const videoTag = this.videoTopImage
			.closest(".video-thumbs")
			.querySelector(".video-tag");
		if (videoTag != null) {
			this.videoType = "local";
			this.playerEl = videoTag;
		} else {
			const iframe = this.videoTopImage
				.closest(".video-thumbs")
				.querySelector(".videoplayer__iframe");
			const iframeHost = new URL(iframe.src).host;

			this.playerEl = iframe;

			switch (iframeHost) {
				case IFRAME_HOSTS.vimeo:
					this.videoType = "vimeo";
					this.loadVimeoSDK();
					break;
				case IFRAME_HOSTS.youtube:
					this.videoType = "youtube";
					this.loadYoutubeSDK();
					break;
			}
		}
	}

	addEventListeners() {
		const self = this;
		this.playerEl.addEventListener(
			"js-video-pause",
			() => {
				self.pauseVideo();
			},
			false
		);
		this.playerEl.addEventListener(
			"js-video-stop",
			() => {
				self.stopVideo();
			},
			false
		);
		if (this.videoType === "local") {
			this.videoTopImage.addEventListener("click", () => {
				if (this.playerEl.paused) {
					self.playLocalVideo();
				} else {
					self.pauseVideo();
				}
			});
		}
	}

	hideThumbnail() {
		this.videoTopImage.classList.add("hidden");
		this.playerEl.tabIndex = 0;
	}

	pauseVideo() {
		this.el.setAttribute("data-playing", "false");

		switch (this.videoType) {
			case "local":
				this.playerEl.pause();
				break;
			case "youtube":
				if (this.YTPlayer) {
					this.playerEl.contentWindow?.postMessage(
						`{"event":"command","func":"pauseVideo","args":""}`,
						"*"
					);
				}
				break;
			case "vimeo":
				if (this.vimeoPlayer) {
					this.vimeoPlayer.pause();
				}
				break;
		}
	}

	stopVideo() {
		this.el.setAttribute("data-playing", "false");

		switch (this.videoType) {
			case "local":
				this.playerEl.stop();
				break;
			case "youtube":
				if (this.YTPlayer) {
					this.playerEl.contentWindow?.postMessage(
						`{"event":"command","func":"stopVideo","args":""}`,
						"*"
					);
				}
				break;
			case "vimeo":
				if (this.vimeoPlayer) {
					this.vimeoPlayer.stop();
				}
				break;
		}
	}

	playVideo() {
		this.el.setAttribute("data-playing", "true");
	}

	playLocalVideo() {
		this.playVideo();

		if (this.autoPlayVideo) {
			this.playerEl.play();
			this.emitPlayEvent();
		} else {
			this.hideThumbnail();
			this.playerEl.load();
			this.playerEl.play();
			this.emitPlayEvent();
		}
	}

	loadYoutubeSDK() {
		const youtubeSDKReadyCallback = () => {
			if (window.YT.loaded) {
				this.playYoutubeVideo();
			} else {
				window.YT.ready(() => {
					this.playYoutubeVideo();
				});
			}
		};

		const youtubeScriptEl = document.getElementById("youtube-iframe-api");
		if (youtubeScriptEl) {
			// already loaded or in the process of loading youtube sdk
			if (window.YT) {
				// already loaded
				youtubeSDKReadyCallback();
			} else {
				// another module initiated the loading of youtube sdk, so we just hook up to that element
				youtubeScriptEl.addEventListener(
					"load",
					youtubeSDKReadyCallback
				);
			}
		} else {
			const tag = document.createElement("script");
			tag.id = "youtube-iframe-api";
			tag.src = "https://www.youtube.com/iframe_api";
			const firstScriptTag = document.getElementsByTagName("script")[0];
			firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
			tag.addEventListener("load", youtubeSDKReadyCallback);
		}
	}

	loadVimeoSDK() {
		const vimeoSDKReadyCallback = () => {
			this.playVimeoVideo();
		};

		const vimeoScriptEl = document.getElementById("vimeo-iframe-api");
		if (vimeoScriptEl) {
			// already loaded or in the process of loading youtube sdk
			if (window.Vimeo) {
				// already loaded
				vimeoSDKReadyCallback();
			} else {
				// another module initiated the loading of youtube sdk, so we just hook up to that element
				vimeoScriptEl.addEventListener("load", vimeoSDKReadyCallback);
			}
		} else {
			const tag = document.createElement("script");
			tag.id = "vimeo-iframe-api";
			tag.src = "https://player.vimeo.com/api/player.js";
			const firstScriptTag = document.getElementsByTagName("script")[0];
			firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
			tag.addEventListener("load", vimeoSDKReadyCallback);
		}
	}

	playVimeoVideo() {
		const self = this;
		this.vimeoPlayer = new Vimeo.Player(this.playerEl);

		let autoplayIsPlaying = true;
		self.videoTopImage.addEventListener("click", function () {
			if (self.autoPlayVideo) {
				if (autoplayIsPlaying) {
					autoplayIsPlaying = false;
					self.pauseVideo();
				} else {
					self.playVideo();
					autoplayIsPlaying = true;
					self.vimeoPlayer.play();
					self.emitPlayEvent();
				}
			} else {
				self.playVideo();
				self.hideThumbnail();
				self.vimeoPlayer.play();
				self.emitPlayEvent();
			}
		});
	}

	playYoutubeVideo() {
		const self = this;
		if (!this.YTPlayer.playVideo) {
			this.YTPlayer = new window.YT.Player(self.playerEl, {
				...(self.autoPlayVideo && {
					playerVars: {
						playsinline: 1,
						autoplay: 1,
						controls: 0,
						fs: 0,
						loop: 1,
						enablejsapi: 1,
						mute: 1,
					},
				}),
			});
		}

		let autoplayIsPlaying = true;
		self.videoTopImage.addEventListener("click", function () {
			if (self.autoPlayVideo) {
				if (autoplayIsPlaying) {
					autoplayIsPlaying = false;
					self.pauseVideo();
				} else {
					self.playVideo();
					autoplayIsPlaying = true;
					self.playerEl.contentWindow?.postMessage(
						`{"event":"command","func":"playVideo","args":""}`,
						"*"
					);
					self.emitPlayEvent();
				}
			} else {
				self.playVideo();
				self.hideThumbnail();
				self.playerEl.contentWindow?.postMessage(
					`{"event":"command","func":"playVideo","args":""}`,
					"*"
				);
				self.emitPlayEvent();
			}
		});
	}

	emitPlayEvent() {
		const playEvent = new CustomEvent("playing", {
			bubbles: true,
			detail: {
				type: this.videoType,
			},
		});
		this.el.dispatchEvent(playEvent);
	}
}
